import React, { useState, useEffect, useRef } from 'react';
import {
    Building,
    MoreVertical,
    Edit,
    Trash2,
    Eye,
    Settings,
    Users,
    Clock,
    Star,
    RefreshCw,
    X,
    Copy,
    Archive
} from 'lucide-react';

const Course = ({ searchKey, idCustomer }) => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(null);
    const dropdownRef = useRef(null);

    // Fetch courses function
    const fetchCourse = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch('https://search.mg.formafusion.io/api/search/courses', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    key: searchKey,
                    idCustomer: idCustomer,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP Error: ${response.status}`);
            }

            const data = await response.json();
            setCourses(data);
        } catch (error) {
            console.error('Error fetching courses:', error);
            setError(error instanceof Error ? error.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (searchKey && idCustomer) {
            fetchCourse();
        }
    }, [searchKey, idCustomer]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(null);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [dropdownOpen]);

    // Action handlers
    const handleEdit = (id) => {
        setDropdownOpen(null);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this course?')) {
            try {
                // Add your delete API call here
                await fetchCourse();
            } catch (error) {
                console.error('Error deleting course:', error);
            }
        }
        setDropdownOpen(null);
    };

    const handleView = (id) => {
        setDropdownOpen(null);
    };

    const handleDuplicate = (id) => {
        setDropdownOpen(null);
    };

    const handleArchive = (id) => {
        setDropdownOpen(null);
    };

    const handleSettings = (id) => {
        setDropdownOpen(null);
    };

    const toggleDropdown = (id) => {
        setDropdownOpen(dropdownOpen === id ? null : id);
    };

    const renderStars = (rating) => {
        return Array.from({ length: 5 }, (_, index) => (
            <Star
                key={index}
                className={`w-4 h-4 ${index < Math.floor(rating) ? 'text-yellow-400 fill-current' :
                    index < rating ? 'text-yellow-400 fill-current opacity-50' : 'text-gray-300'}`}
            />
        ));
    };

    const getLevelColor = (level) => {
        switch (level?.toLowerCase()) {
            case 'débutant':
            case 'beginner':
                return 'bg-green-100 text-green-800';
            case 'intermédiaire':
            case 'intermediate':
                return 'bg-blue-100 text-blue-800';
            case 'avancé':
            case 'advanced':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Loading component
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-64">
                <div className="flex flex-col items-center space-y-4">
                    <div className="w-12 h-12 border-4 border-purple-200 rounded-full border-t-purple-600 animate-spin"></div>
                    <p className="text-gray-600">Loading courses...</p>
                </div>
            </div>
        );
    }

    // Error component
    if (error) {
        return (
            <div className="p-8 text-center bg-red-50 rounded-xl">
                <div className="flex flex-col items-center space-y-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
                        <X className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-red-900">Error</h3>
                        <p className="text-red-700">{error}</p>
                    </div>
                    <button
                        onClick={fetchCourse}
                        className="flex items-center px-6 py-3 space-x-2 text-white transition-colors bg-red-600 rounded-lg hover:bg-red-700"
                    >
                        <RefreshCw className="w-4 h-4" />
                        <span>Retry</span>
                    </button>
                </div>
            </div>
        );
    }

    // No courses found
    if (!courses || courses.length === 0) {
        return (
            <div className="p-12 text-center bg-gray-50 rounded-xl">
                <div className="flex flex-col items-center space-y-4">
                    <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full">
                        <Building className="w-8 h-8 text-gray-400" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">No Courses Found</h3>
                        <p className="text-gray-600">No courses found for "{searchKey}"</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Courses</h1>
                    <p className="mt-1 text-gray-600">
                        You have {courses.length} course{courses.length > 1 ? 's' : ''}
                    </p>
                </div>
                <button
                    onClick={fetchCourse}
                    className="flex items-center px-6 py-3 space-x-2 text-white transition-colors bg-purple-600 rounded-lg shadow-sm hover:bg-purple-700"
                >
                    <RefreshCw className="w-4 h-4" />
                    <span>Refresh</span>
                </button>
            </div>

            {/* Courses Grid */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
                {courses.map((course) => (
                    <div
                        key={course.id}
                        className="overflow-hidden transition-all duration-300 bg-white border border-gray-200 shadow-sm rounded-2xl hover:shadow-xl group"
                    >
                        {/* Course Image and Level */}
                        <div className="relative">
                            <div className="flex items-center justify-center h-48 bg-gradient-to-br from-purple-100 to-blue-100">
                                {course.image ? (
                                    <img
                                        src={course.image}
                                        alt={course.title}
                                        className="object-cover w-full h-full"
                                    />
                                ) : (
                                    <Building className="w-16 h-16 text-purple-300" />
                                )}
                            </div>
                            <div className="absolute top-4 right-4">
                                <span className={`px-3 py-1 text-sm rounded-full font-medium ${getLevelColor(course.level)}`}>
                                    {course.level || 'Beginner'}
                                </span>
                            </div>

                            {/* Dropdown Menu */}
                            <div className="absolute top-4 left-4" ref={dropdownRef}>
                                <a href={`https://catalogue.forma-fusion.com/cfp/modules/${course.id}`} className="transition-colors shadow-sm bg-white/90 backdrop-blur-sm rounded-xl hover:bg-white">
                                    <Eye className="w-6 h-6 text-gray-900" />
                                </a>
                            </div>
                        </div>

                        {/* Course Content */}
                        <div className="p-6 space-y-4">
                            {/* Title */}
                            <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 min-h-[3.5rem]">
                                {course.title}
                            </h3>

                            {/* Duration */}
                            <div className="flex items-center space-x-2 text-gray-600">
                                <Clock className="w-4 h-4" />
                                <span className="text-sm">
                                    {course.dureeJ} jours ({course.dureeH} heures)
                                </span>
                            </div>

                            {/* Rating */}
                            <div className="flex items-center space-x-2">
                                <div className="flex items-center space-x-1">
                                    {renderStars(course.note || 0)}
                                </div>
                                <span className="text-sm text-gray-600">
                                    {course.note || '0.0'} ({course.totalAvis || 0} reviews)
                                </span>
                            </div>

                            {/* Stats and Price */}
                            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                <div className="flex items-center space-x-2">
                                    <Users className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-600">
                                        {course.totalFormed || 0} formés
                                    </span>
                                </div>
                                <div className="text-right">
                                    <span className="text-lg font-bold text-gray-900">
                                        {course.prix || 0} Ar
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Course;